import "./App.css";
import Tags from "./components/Tags";
import TypeWriter from "./components/TypeWriter";
import TypeWriterManager from "./components/TypeWriterManager";
import Header from "./components/Header";
import Footer from "./components/Footer";
import OnlineStatus from "./components/OnlineStatus";
import PreviewBanner from "./components/PreviewBanner";
import Notification from "./components/Notification";
import Link from "./components/Link";
import { ThemeProvider } from "./components/ThemeProvider";
import { audioEffects } from "./utils/audioEffects";
import { useSparklingEffect } from "./hooks/useSparklingEffect";
import {
  unlockEasterEgg,
  resetEasterEggProgress,
  devUnlockEasterEgg,
  getUnlockedEasterEggDetails,
} from "./utils/easterEggUnlocks";
import {
  getEasterEggConfig,
  createEasterEggNotificationHandler,
  createHintNotificationHandler,
} from "./config/easterEggs";
import { useState, useRef, useEffect } from "react";

function App() {
  // Preload all profile images to prevent jump after flip
  useEffect(() => {
    const imagesToPreload = [
      "images/luuk-avatar.png",
      "images/Luuk-Verboeket.jpg",
      "images/luuk-avatar-offline.png",
    ];
    imagesToPreload.forEach((src) => {
      const img = new window.Image();
      img.src = src;
    });
  }, []);
  const greetingTexts = [
    "Luuk Verboeket",
    "een Front-End Developer",
    "een fijne knaap",
    "een Web Designer",
    "blij dat je er bent :)",
  ];

  const [showAvatarMode, setShowAvatarMode] = useState(() => {
    // Default to avatar mode, but allow localStorage to override
    const savedState = localStorage.getItem("avatar_mode_active");
    return savedState !== null ? savedState === "true" : true;
  });
  const [onlineStatus, setOnlineStatus] = useState<
    "online" | "shutting-off" | "offline" | "waking-up"
  >("online");
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showAudioNotification, setShowAudioNotification] = useState(false);
  const [showTypeWriterManager, setShowTypeWriterManager] = useState(false);
  const [currentGreetingTexts, setCurrentGreetingTexts] =
    useState(greetingTexts);
  const [showEasterEggNotification, setShowEasterEggNotification] = useState<
    string | null
  >(null);
  const [showHintNotification, setShowHintNotification] = useState<
    string | null
  >(null);
  const currentAudio = useRef<HTMLAudioElement | null>(null);
  const easterEggTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hintTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { startSparklingEffect, stopSparklingEffect } = useSparklingEffect();

  // Create centralized notification handlers
  const handleEasterEggUnlock = createEasterEggNotificationHandler({
    setShowEasterEggNotification,
    setShowHintNotification,
    easterEggTimeoutRef,
    hintTimeoutRef,
  });

  const handleShowHint = createHintNotificationHandler({
    setShowEasterEggNotification,
    setShowHintNotification,
    easterEggTimeoutRef,
    hintTimeoutRef,
  });

  // Listen for mute state changes and stop playing audio if muted
  useEffect(() => {
    const checkMuteState = () => {
      if (audioEffects.isMutedState() && currentAudio.current) {
        handleStopAudio();
      }
    };

    // Check periodically for mute state changes
    const interval = setInterval(checkMuteState, 100);

    return () => clearInterval(interval);
  }, []);

  // Save avatar mode state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("avatar_mode_active", showAvatarMode.toString());
  }, [showAvatarMode]);

  // Parallax effect for hero light ring
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const parallaxOffset = scrollY * 0.3; // Adjust multiplier for parallax intensity

      // Update CSS custom property for parallax offset
      document.documentElement.style.setProperty(
        "--parallax-offset",
        `${parallaxOffset}px`,
      );
    };

    // Add scroll listener
    window.addEventListener("scroll", handleScroll);

    // Cleanup
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Ensure audio interaction tracking is initialized for mobile sound reliability
  useEffect(() => {
    audioEffects.initializeInteractionTracking();
  }, []);

  const handleProfileClick = () => {
    const currentTime = Date.now();
    const timeDiff = currentTime - lastClickTime;

    // Reset click count if too much time has passed (more than 500ms)
    if (timeDiff > 500) {
      setClickCount(1);
    } else {
      setClickCount((prev) => prev + 1);
    }

    setLastClickTime(currentTime);

    // Clear any existing timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    // Check for double click
    if (clickCount >= 1 && timeDiff <= 500) {
      // Double click detected - trigger flip animation and toggle avatar mode
      setIsFlipping(true);

      // Play first swoosh sound effect immediately
      audioEffects.playSwoosh();

      // Play second swoosh at the midpoint when image changes
      setTimeout(() => {
        audioEffects.playSwoosh2();
      }, 300);

      // Change the image at the midpoint of the flip (when it's sideways)
      setTimeout(() => {
        const newAvatarMode = !showAvatarMode;
        setShowAvatarMode(newAvatarMode);
        // Unlock Kickflip easter egg on any flip, if not already unlocked
        if (!localStorage.getItem("avatar_mode_unlocked")) {
          const avatarEasterEgg = getEasterEggConfig("avatar_mode");
          const wasNewlyUnlocked = unlockEasterEgg(
            avatarEasterEgg?.id || "avatar_mode",
          );
          if (wasNewlyUnlocked) {
            setTimeout(() => {
              handleEasterEggUnlock("Kickflip!");
            }, 500);
          }
        }
      }, 300); // Change image at midpoint of flip animation

      // Remove flip animation class after animation completes
      setTimeout(() => {
        setIsFlipping(false);
      }, 600);

      // Reset click count
      setClickCount(0);
    } else {
      // Set timeout to reset click count if no more clicks come
      clickTimeoutRef.current = setTimeout(() => {
        setClickCount(0);
      }, 500);
    }
  };

  const handleAudioPlay = (e: React.MouseEvent) => {
    e.preventDefault();

    // Don't play if sounds are muted
    if (audioEffects.isMutedState()) {
      return;
    }

    // Unlock the Village People easter egg
    const villagePeopleEasterEgg = getEasterEggConfig("village_people");
    const wasNewlyUnlocked = unlockEasterEgg(
      villagePeopleEasterEgg?.id || "village_people",
    );
    if (wasNewlyUnlocked) {
      // Show easter egg notification after a brief delay
      setTimeout(() => {
        handleEasterEggUnlock("Village People");
      }, 1000); // Show after audio notification appears
    }

    // Stop any currently playing audio
    if (currentAudio.current) {
      currentAudio.current.pause();
      currentAudio.current.currentTime = 0;
    }

    // Create new audio instance
    const audio = new Audio("audio/the-village-people.mp3");
    audio.volume = 0.2; // Set volume to half
    currentAudio.current = audio;

    // Show notification
    setShowAudioNotification(true);

    // Play audio
    audio.play().catch((err) => console.error("Audio playback failed:", err));

    // Hide notification when audio ends
    audio.addEventListener("ended", () => {
      setShowAudioNotification(false);
      currentAudio.current = null;
    });
  };

  const handleStopAudio = () => {
    if (currentAudio.current) {
      currentAudio.current.pause();
      currentAudio.current.currentTime = 0;
      currentAudio.current = null;
    }
    setShowAudioNotification(false);
  };

  // Handle online status changes from OnlineStatus component
  const handleStatusChange = (
    status: "online" | "shutting-off" | "offline" | "waking-up",
  ) => {
    setOnlineStatus(status);
  };

  // Get the appropriate avatar image based on mode and online status
  const getAvatarImage = () => {
    if (showAvatarMode) {
      // In avatar mode, check if offline
      if (onlineStatus === "offline") {
        return "images/luuk-avatar-offline.png";
      }
      return "images/luuk-avatar.png";
    }
    // Regular photo mode
    return "images/Luuk-Verboeket.jpg";
  };

  // Development environment alert
  if (__IS_PREVIEW__) {
    console.warn(
      "🚧 DEVELOPMENT ENVIRONMENT - You are viewing the development version of this site",
    );
  }

  // Function to get the appropriate preview banner message
  const getPreviewMessage = () => {
    // Check if we're on localhost
    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    ) {
      return "🔧 Local development";
    }
    // Check if we're on Cloudflare Pages develop branch
    if (
      window.location.hostname.includes("develop") ||
      window.location.href.includes("develop")
    ) {
      return "🚀 Development branch";
    }
    // Default fallback
    return "🚧 Development environment";
  };

  // Add development helpers to window for testing
  useEffect(() => {
    if (typeof window !== "undefined") {
      // @ts-expect-error - Adding dev helpers to window
      window.devEasterEggs = {
        unlock: devUnlockEasterEgg,
        reset: resetEasterEggProgress,
        list: () => {
          console.table(getUnlockedEasterEggDetails());
        },
        unlockVillagePeople: () => devUnlockEasterEgg("village_people"),
        unlockCustomMessage: () => devUnlockEasterEgg("custom_message"),
      };
      console.log("🛠️ Dev helpers available: window.devEasterEggs");
    }
  }, []);

  return (
    <ThemeProvider>
      <div className="app">
        {__IS_PREVIEW__ && <PreviewBanner message={getPreviewMessage()} />}
        {showAudioNotification && (
          <Notification
            message="Born to be alive - The Village People"
            type="audio"
            position="top-left"
            autoClose={false}
            clickable={true}
            onClose={handleStopAudio}
            onClick={handleStopAudio}
          />
        )}
        {showEasterEggNotification && (
          <Notification
            message={`🏅 ${showEasterEggNotification} unlocked!`}
            type="success"
            position="top-right"
            autoClose={true}
            duration={4000}
            onClose={() => setShowEasterEggNotification(null)}
          />
        )}
        {showHintNotification && (
          <Notification
            message={`💡 ${showHintNotification}`}
            type="info"
            position="bottom-right"
            autoClose={true}
            duration={3000}
            onClose={() => setShowHintNotification(null)}
          />
        )}
        <Header onShowHint={handleShowHint} />
        <main className="main-content">
          {/* Hero Section */}
          <section className="hero">
            <div className="hero-content">
              <h1
                className="hero-title"
                onClick={() => setShowTypeWriterManager(true)}
                title="Pas teksten aan"
              >
                <TypeWriter
                  staticTextBefore="Hey! Welkom!<br>Ik ben"
                  texts={currentGreetingTexts}
                  typingSpeed={80}
                  pauseDuration={2500}
                  onTextsChange={setCurrentGreetingTexts}
                />
                <span className="edit-icon"></span>
              </h1>
            </div>
          </section>

          {/* About Me Section */}
          <section className="about">
            <div className="about-grid">
              <div className="profile-image">
                <div className="profile-container">
                  <img
                    src={getAvatarImage()}
                    alt="Profiel foto"
                    className={`profile-photo ${isFlipping ? "flipping" : ""}`}
                    onClick={handleProfileClick}
                    style={{ cursor: "pointer" }}
                  />
                  <OnlineStatus onStatusChange={handleStatusChange} />
                </div>
              </div>
              <div className="about-content">
                <h2>Wie is deze knaap?</h2>
                <p>
                  Yo! Ik ben Luuk, een front-end webdeveloper uit het zuiden van
                  Nederland. Ik ben geboren en getogen in{" "}
                  <span
                    className="easter-egg-link"
                    onClick={handleAudioPlay}
                    onMouseEnter={(e) => startSparklingEffect(e.currentTarget)}
                    onMouseLeave={stopSparklingEffect}
                  >
                    Born
                  </span>
                  , maar woon nu in Heerlen-city.
                </p>
                <p>
                  Ik heb nu{" "}
                  {(
                    Math.round(
                      (new Date().getFullYear() +
                        (new Date().getMonth() + 1) / 12 -
                        (2022 + 4 / 12)) *
                        2,
                    ) / 2
                  )
                    .toString()
                    .replace(".", ",")}{" "}
                  jaar werkervaring als webdeveloper en -designer, opgedaan in
                  loondienst én als freelancer.
                </p>
                <p>
                  In die tijd heb ik gemerkt dat ik het 't fijnst vind om gewoon
                  goed met mensen aan tafel te zitten. Geen stijve briefings of
                  ingewikkeld gedoe, maar samen sparren en babbelen om uit te
                  vogelen wat er echt nodig is, zodat ik dat daarna kan vertalen
                  naar een vet eindproduct.
                </p>
                <p>
                  Als ik niet achter mijn scherm zit, vind je me waarschijnlijk
                  met een kendama in mijn hand, op het skatepark of bezig met
                  muziek (drummen, gitaar spelen of mixen). Daarnaast steek ik
                  graag tijd in{" "}
                  <Link
                    title="OldSchool RuneScape HiScores for Huell"
                    href="https://secure.runescape.com/m=hiscore_oldschool/compare?user1=Huell"
                    target="_blank"
                  >
                    OldSchool RuneScape
                  </Link>
                  , een game die ik nu al{" "}
                  {Math.round(
                    ((new Date().getFullYear() +
                      (new Date().getMonth() + 1) / 12 -
                      (2005 + 8 / 12)) /
                      (new Date().getFullYear() +
                        (new Date().getMonth() + 1) / 12 -
                        (1998 + 3 / 12))) *
                      100,
                  )}
                  {"% "}
                  van mijn leven speel.
                </p>
                <Tags
                  tags={[
                    "React",
                    "TypeScript",
                    "Node.js",
                    "CSS",
                    "PHP",
                    "WordPress CMS",
                  ]}
                  title="Dingen die ik kan typen"
                />
                <Tags
                  tags={[
                    "Drummen",
                    "Gitaar spelen",
                    "Skaten",
                    "Mario Kart toernooien winnen",
                  ]}
                  title="Andere dingen die ik kan"
                />
                <Tags
                  tags={[
                    "Maxed op OSRS",
                    "Top 5 gamers van Nederland",
                    "Gekust door Xander de Buisonjé",
                  ]}
                  title="Dingen waar ik trots op ben"
                />
              </div>
            </div>
          </section>
        </main>
        <Footer />

        {/* TypeWriter Manager Modal */}
        <TypeWriterManager
          isOpen={showTypeWriterManager}
          onClose={() => setShowTypeWriterManager(false)}
          defaultTexts={greetingTexts}
          onTextsChange={setCurrentGreetingTexts}
          onEasterEggUnlock={handleEasterEggUnlock}
        />
      </div>
    </ThemeProvider>
  );
}

export default App;
